import { elevation, themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { getResponsiveSpacing, spacing } from "@dana/MobiusOverrides/styles/spacing";
import { fontSizes, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { productCarouselStyles } from "@dana/Overrides/Widgets/Common/ProductCarousel";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import { StyledTypography } from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { css } from "styled-components";

productCarouselStyles.productCarouselContainer = mergeToNew(productCarouselStyles.productCarouselContainer, {
    css: css`
        max-width: 1440px;
        margin: 0 auto;

        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        padding: 40px 16px;
                    `,
                    css`
                        padding: 0 16px;
                    `,
                    css`
                        padding: 0 24px;
                    `,
                    css`
                        padding: 0 40px;
                    `,
                ],
                "min",
            )}
        ${getResponsiveSpacing("py", "xLarge")};

        @media print {
            padding: 0 0 16px;
        }
    `,
});

productCarouselStyles.titleText = mergeToNew(productCarouselStyles.titleText, {
    css: css`
        margin-bottom: 24px;
    `,
});

productCarouselStyles.prevArrowButton = mergeToNew(productCarouselStyles.prevArrowButton, {
    shape: "rectangle",
    css: css`
        padding: 0;
        min-width: auto;
        min-height: auto;
        z-index: 2;
        justify-content: center;
        background: none;
        border: none;

        ${IconWrapper} {
            svg {
                path {
                    fill: ${themeColors.secondary.danaDarkBlue};
                }
            }
        }

        &:not(:disabled) {
            &:hover {
                background: transparent;
                border: none;

                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeColors.secondary.danaBrightBlue};
                        }
                    }
                }
            }

            &:focus {
                outline: none;
            }

            &:focus-visible {
                background: transparent;
                border: 1px solid ${themeColors.primary.white};
                box-shadow: ${elevation.focus};
                outline: none;

                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeColors.secondary.danaBrightBlue};
                        }
                    }
                }
            }
        }

        &:disabled {
            background: none;
            border: none;
            ${IconWrapper} {
                svg {
                    path {
                        fill: ${themeBackgrounds.darkGray};
                    }
                }
            }
        }
    `,
});

productCarouselStyles.nextArrowButton = mergeToNew(productCarouselStyles.nextArrowButton, {
    shape: "rectangle",
    css: css`
        padding: 0;
        min-width: auto;
        min-height: auto;
        z-index: 2;
        justify-content: center;
        background: none;
        border: none;

        ${IconWrapper} {
            svg {
                path {
                    fill: ${themeColors.secondary.danaDarkBlue};
                }
            }
        }

        &:not(:disabled) {
            &:hover {
                background: transparent;
                border: none;

                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeColors.secondary.danaBrightBlue};
                        }
                    }
                }
            }

            &:focus {
                outline: none;
            }

            &:focus-visible {
                background: transparent;
                border: 1px solid ${themeColors.primary.white};
                box-shadow: ${elevation.focus};
                outline: none;

                ${IconWrapper} {
                    svg {
                        path {
                            fill: ${themeColors.secondary.danaBrightBlue};
                        }
                    }
                }
            }
        }

        &:disabled {
            background: none;
            border: none;
            ${IconWrapper} {
                svg {
                    path {
                        fill: ${themeBackgrounds.darkGray};
                    }
                }
            }
        }
    `,
});

productCarouselStyles.carouselContainer = mergeToNew(productCarouselStyles.carouselContainer, {
    css: css`
        padding-bottom: 2px;
    `,
});

productCarouselStyles.carouselGridItem = mergeToNew(productCarouselStyles.carouselGridItem, {
    width: 12,
    css: css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    flex-basis: 100%;
                    max-width: 100%;
                `,
                css`
                    flex-basis: 100%;
                    max-width: 100%;
                `,
                css`
                    flex-basis: 100%;
                    max-width: 100%;
                `,
                css`
                    flex-basis: 100%;
                    max-width: 100%;
                `,
                css`
                    flex-basis: 100%;
                    max-width: 100%;
                `,
            ])}
    `,
});

productCarouselStyles.carouselSlidesContainer = mergeToNew(productCarouselStyles.carouselSlidesContainer, {
    css: css`
        @media print {
            transform: none !important;
            display: block;
        }
    `,
});

productCarouselStyles.carouselSlide = mergeToNew(productCarouselStyles.carouselSlide, {
    css: css`
        padding-left: 8px;
        padding-right: 8px;

        @media print {
            width: 25% !important;
            float: left;
            margin-bottom: 16px;
            height: 275px;
        }
    `,
});

productCarouselStyles.carouselSlideInner = mergeToNew(productCarouselStyles.carouselSlideInner, {
    css: css`
        border: 1px solid ${themeBackgrounds.lightGray};
        padding: 24px;

        &:hover,
        &:focus-visible {
            border-color: ${themeColors.primary.danaBlue};
            box-shadow: ${elevation.hover};
            outline: none;
        }

        text-decoration: none;

        ${StyledTypography} {
            text-decoration: none;
        }

        &:hover {
            text-decoration: none;
            ${StyledTypography} {
                text-decoration: none;
            }
        }
        @media print {
            padding: 8px;
        }
    `,
});

productCarouselStyles.carouselProductStyles = mergeToNew(productCarouselStyles.carouselProductStyles, {
    badgeAreaWrapper: {
        css: css`
            position: initial;
            
            .badge-wrapper {
                top: 8px;
                right: -8px;
                overflow: visible;
                width: auto;

                [class*="TextBadge"] {
                    padding: 4px 16px;
                    position: relative;

                    &::after {
                        content: "";
                        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='10' viewBox='0 0 7 10' fill='none'%3E%3Cpath d='M-4.69387e-05 9.78115L-0.000116752 0.781067L7.00007 9.78119L-4.69387e-05 9.78115Z' fill='%23535353'/%3E%3C/svg%3E");
                        background-repeat: no-repeat;
                        position: absolute;
                        top: -10px;
                        right: -2px;
                        width: 10px;
                        height: 14px;
                        z-index: 5;
                    }
                }
            }
        `,
    },
    productDescriptionStyles: {
        productDetailDescription: {
            size: fontSizes.large,
            lineHeight: lineHeights.defaultLarge,
            color: themeColors.primary.richBlack,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                margin-top: 24px;
            `,
        },
        erpNumberGridItem: {
            css: css`
                padding: 0;
            `,
        },
        erpNumberValueText: {
            variant: "h3",
            color: themeColors.primary.danaBlue,
            css: css`
                margin-bottom: 0;
                &:hover {
                    color: ${themeColors.secondary.danaDarkBlue};
                    text-decoration: none;
                }
            `,
        },
    },
});

productCarouselStyles.carouselArrowWrapper = mergeToNew(productCarouselStyles.carouselArrowWrapper, {
    css: css`
        width: 100%;
        max-width: 1440px;
        margin: 0 auto;
        margin-bottom: 16px;
        display: flex;
        column-gap: 8px;
        justify-content: flex-end;
    `,
});

productCarouselStyles.carouselProductStyles = mergeToNew(productCarouselStyles.carouselProductStyles, {
    productImageStyles: {
        img: {
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            min-height: 310px;
                            max-height: 310px;
                            object-fit: contain;
                        `,
                        css`
                            min-height: 310px;
                            max-height: 310px;
                            object-fit: contain;
                        `,
                        css`
                            min-height: 337px;
                            max-height: 337px;
                            object-fit: contain;
                        `,
                        css`
                            min-height: 280px;
                            max-height: 280px;
                            object-fit: contain;
                        `,
                        css`
                            min-height: 280px;
                            max-height: 280px;
                            object-fit: contain;
                        `,
                    ])}

                    @media print {
                        min-height: 120px;
                        max-height: 120px;
                    }
            `,
        },
    },
});
